import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { formatTime, getMilestoneMessage } from '../utils/timerLogic';
import SafeIcon from '../common/SafeIcon';
import { FiXCircle, FiPause, FiPlay, FiCheckCircle } from 'react-icons/fi';
import Confetti from './Confetti';

const ActiveTimer = ({ duration, reminders, themeColor, onStop }) => {
  const [timeLeft, setTimeLeft] = useState(duration);
  const [isRunning, setIsRunning] = useState(true);
  const [currentMessage, setCurrentMessage] = useState('Get ready...');

  useEffect(() => {
    const initialMessage = getMilestoneMessage(duration, duration, reminders);
    if (initialMessage) setCurrentMessage(initialMessage);
  }, [duration, reminders]);

  useEffect(() => {
    if (!isRunning || timeLeft <= 0) {
      if (timeLeft === 0) setIsRunning(false);
      return undefined;
    }

    const interval = setInterval(() => {
      setTimeLeft((previousTime) => {
        const nextTime = previousTime - 1;
        const newMessage = getMilestoneMessage(nextTime, duration, reminders);

        if (newMessage) setCurrentMessage(newMessage);
        return nextTime;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [isRunning, timeLeft, duration, reminders]);

  const progress = ((duration - timeLeft) / duration) * 100;
  const isComplete = timeLeft === 0;
  const isWarning = timeLeft <= 120 && duration > 300;

  return (
    <>
      {isComplete && <Confetti />}
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="relative z-10 mx-auto flex w-full max-w-4xl flex-col items-center justify-center px-4"
      >
        <div className="mb-12 h-2 w-full max-w-2xl overflow-hidden rounded-full bg-[#d7eaf7] shadow-inner">
          <motion.div
            className="h-full rounded-full"
            style={{ backgroundColor: themeColor, width: `${progress}%` }}
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ ease: 'linear' }}
          />
        </div>

        <div
          className={`text-[6rem] font-bold leading-none tracking-tighter tabular-nums transition-colors duration-1000 sm:text-[10rem] md:text-[16rem] ${
            isWarning ? 'text-[#d38a57]' : 'text-[#245b86]'
          }`}
        >
          {formatTime(timeLeft)}
        </div>

        <div className="mt-8 flex h-24 items-center justify-center text-center">
          <AnimatePresence mode="wait">
            <motion.p
              key={currentMessage}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className={`max-w-2xl text-2xl font-medium sm:text-3xl ${
                isWarning ? 'text-[#b8754a]' : 'text-[#376b92]'
              }`}
            >
              {isComplete ? 'Great work! You completed your session.' : currentMessage}
            </motion.p>
          </AnimatePresence>
        </div>

        {isComplete && (
          <div className="mt-4 flex items-center gap-2 font-semibold text-[#4a8ccc]">
            <SafeIcon icon={FiCheckCircle} />
            Session complete
          </div>
        )}

        <div className="mt-12 flex gap-6">
          <button
            onClick={() => setIsRunning(!isRunning)}
            disabled={isComplete}
            className="rounded-full border border-[#c9e1f3] bg-white/80 p-4 text-[#4a8ccc] shadow-sm transition hover:scale-105 hover:bg-white disabled:opacity-50"
            title={isRunning ? 'Pause' : 'Resume'}
          >
            <SafeIcon
              icon={isRunning ? FiPause : FiPlay}
              className="text-3xl"
            />
          </button>
          <button
            onClick={onStop}
            className="rounded-full border border-[#f0cccc] bg-white/80 p-4 text-[#d47b7b] shadow-sm transition hover:scale-105 hover:bg-[#fff1f1]"
            title="End Session"
          >
            <SafeIcon icon={FiXCircle} className="text-3xl" />
          </button>
        </div>
      </motion.div>
    </>
  );
};

export default ActiveTimer;